import { ReactNode, useEffect, useRef } from 'react';
import { Popup, PopupProps } from 'semantic-ui-react';
import { useImageStoreImage } from '../../stores/imageStore';
import { BoundingBoxType } from '../../types';
import { IMAGE_POPUP_MODE } from './constants';
import './imagePopup.styles.css';

type ImagePopupModeType = (typeof IMAGE_POPUP_MODE)[keyof typeof IMAGE_POPUP_MODE];

// Convert percentage to pixels
const percentToPixels = (percent: number, dimension: number): number => {
    return (percent / 100) * dimension;
};

type ImagePopupBasePropsType<T extends ImagePopupModeType> = {
    coordinates: BoundingBoxType;
    trigger: ReactNode;
    className?: string;
    mode?: T;
};

type RequireOpenProp<T> = T extends typeof IMAGE_POPUP_MODE.CONTROLLED ? { open: boolean } : { open?: boolean };

type ImagePopupPropsType<T extends ImagePopupModeType> = ImagePopupBasePropsType<T> & RequireOpenProp<T>;

function ImagePopup<T extends ImagePopupModeType>({
    trigger,
    className = '',
    mode,
    open,
    coordinates,
}: ImagePopupPropsType<T>) {
    const props: PopupProps = {
        /*
         * Issue: Hover event not triggering as expected due to popup behavior.
         *
         * Problem Explanation:
         * The popup is rendered over the element we want to hover on. When moving the cursor
         * toward the desired element, there is a brief period where the cursor is still over
         * the popup before it disappears. This causes the following issue:
         *
         * 1. The cursor is over the popup, and then the popup disappears.
         * 2. Since the cursor is already over the desired element when the popup is removed,
         *    the `pointerenter` or `mouseover` event does not trigger because these events
         *    only fire when the cursor enters the boundary of an element.
         *
         * Expected Behavior:
         * We want the `mouseover` event to trigger after the popup disappears to ensure the
         * hover interaction behaves as intended.
         *
         * Solution:
         * To address this, we remove the margins of the popup element and add an additional
         * offset to create a small gap between the currently hovered element and the desired
         * element. This forces the cursor to momentarily leave the area before entering the
         * desired element, correctly triggering the hover event.
         */
        offset: [0, 12],
        on: 'hover',
        position: 'top right',
        className: `image-popup ${className}`,
        content: <ImagePopupContent coordinates={coordinates} />,
        trigger,
    };

    if (mode === IMAGE_POPUP_MODE.CONTROLLED) {
        props.open = open;
    }

    return <Popup {...props} />;
}

type ImagePopupContentPropsType = {
    coordinates: BoundingBoxType;
};

function ImagePopupContent({ coordinates }: ImagePopupContentPropsType) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const image = useImageStoreImage();

    useEffect(() => {
        if (!image || !canvasRef.current) {
            return;
        }

        const canvas = canvasRef.current;
        const canvasContext = canvas.getContext('2d');

        if (!canvasContext) {
            return;
        }

        const x = percentToPixels(coordinates.left * 100, image.width);
        const y = percentToPixels(coordinates.top * 100, image.height);
        const width = percentToPixels(coordinates.width * 100, image.width);
        const height = percentToPixels(coordinates.height * 100, image.height);
        canvas.width = width;
        canvas.height = height;

        canvasContext.drawImage(image, x, y, width, height, 0, 0, width, height);
    }, [coordinates, image]);

    return (
        <div className="image-popup__image-container">
            <canvas className="image-popup__image" ref={canvasRef}></canvas>
        </div>
    );
}

export default ImagePopup;
